import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import axiosInstance from '../../Axios';
import Toast from '../../services/Toast';
import { useBrand } from '../../hooks/useBrand';
import { withApplication } from '../../services/PendingApplication';
import ProductsTagline from '../products-tagline/ProductsTagline';
import './ForgotPasswordForm.css';

interface ForgotPasswordFormProps {
  onSuccess: (data: { id: number; email: string }) => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const { brand, isBranded, style: brandStyle } = useBrand();

  const handleSubmit = async () => {
    if (!username.trim()) {
      setIsInputFocused(true);
      Toast.error('Please enter your username');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post('/auth/forgot-password', {
        username: username.trim()
      });

      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setLoading(false);
        return;
      }

      Toast.success('OTP sent to your registered email address.');
      onSuccess(response.data.data);
    } catch (error: any) {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-card" style={brandStyle} data-brand={brand.key || 'nextek'}>
      <div className="forgot-card__inner">
        {/* Decorative elements */}
        <div className="forgot-card__decoration">
          <div className="forgot-card__circle forgot-card__circle--1" />
          <div className="forgot-card__circle forgot-card__circle--2" />
        </div>

        {/* Logo — same per-product lockup as the sign-in card, so a user who
            came from LinenTech and clicked Forgot Password doesn't watch the
            branding revert to Nextek mid-flow. */}
        <div className="forgot-card__logo">
          <img
            src={brand.logo}
            alt={brand.name}
            style={{ height: `${brand.logoHeight}px` }}
            data-cy="forgot-brand-logo"
          />
          {isBranded && (
            <p className="forgot-card__parent-brand">
              A product of <strong>Nextek Systems</strong>
            </p>
          )}
        </div>

        {/* Icon */}
        <div className="forgot-card__icon">
          <div className="forgot-card__icon-bg">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4"/>
              <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"/>
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="forgot-card__header">
          <h1 className="forgot-card__title">
            <span className="forgot-card__title-accent">Forgot</span> Password?
          </h1>
          <p className="forgot-card__subtitle">
            Enter your username and we'll send you an OTP to reset your password
          </p>
        </div>

        {/* Form */}
        <form className="forgot-form" onSubmit={(e) => e.preventDefault()} data-cy="forgot-password-form">
          <div className={`forgot-input-group ${isInputFocused ? 'forgot-input-group--focused' : ''} ${username ? 'forgot-input-group--filled' : ''}`}>
            <div className="forgot-input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M20 21a8 8 0 1 0-16 0"/>
              </svg>
            </div>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="forgot-input"
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              data-cy="forgot-password-identifier-input"
            />
            <div className="forgot-input-highlight" />
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className={`forgot-btn ${loading ? 'forgot-btn--loading' : ''}`}
            data-cy="forgot-password-submit-btn"
          >
            {loading ? (
              <div className="forgot-btn__spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              <>
                Send OTP
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/>
                  <path d="m12 5 7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </form>

        {/* Back to login */}
        <div className="forgot-back">
          <NavLink to={withApplication('/')} className="forgot-back-link" data-cy="forgot-password-back-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7"/>
              <path d="M19 12H5"/>
            </svg>
            Back to Login
          </NavLink>
        </div>

        {/* Footer */}
        <div className="forgot-footer">
          <ProductsTagline signingInTo={isBranded ? brand.name : null} />
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
